/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Search_ProductHit as SearchProductHit } from '__generated__/graphql-types';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';

import { Chip, ChipGroup, TextField } from '@coursera/cds-core';
import { SearchIcon } from '@coursera/cds-icons';
import { StyleSheet, breakPoint } from '@coursera/coursera-ui';

import NumberOfResultsSection from 'bundles/ent-website/components/CourseSearch//NumberOfResultsSection';
import InPageSearchResults from 'bundles/ent-website/components/CourseSearch/InPageSearchResults';
import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/ent-website/components/CourseSearch/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { COURSERA_PLUS_POPULAR_DOMAINS_MAPPING } from 'bundles/search-common/constants';

import _t from 'i18n!nls/ent-website';

const styles = StyleSheet.create({
  searchResults: {
    marginTop: '48px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '24px',
    },
  },
});

type State = {
  currentSearchQuery?: string | null;
  currentSearchInput?: string | null;
};

type PropsFromCaller = {
  onSearchQueryChange?: (query: string) => void;
  searching: boolean;
  results?: SearchProductHit[];
};

type Props = PropsFromCaller;

// The domain maps are borrowed from coursera plus but we discovered
// they collapse better in mobile resolutions in a slightly different order.
const rearrangedDomainMaps = [
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[0],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[1],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[2],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[4],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[3],
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING[5],
];

export class CourseSearch extends React.Component<Props, State> {
  static contextTypes = {
    _eventData: PropTypes.object,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof CourseSearch.contextTypes>;

  static childContextTypes = {
    router: PropTypes.object.isRequired,
  };

  state = {
    currentSearchQuery: '',
    currentSearchInput: '',
  };

  getChildContext() {
    const { router } = this.context;

    return {
      router,
    };
  }

  componentDidUpdate(_: unknown, prevState: State) {
    const { currentSearchQuery } = this.state;
    const { _eventData } = this.context;

    if (prevState.currentSearchQuery !== currentSearchQuery) {
      Retracked.trackComponent(
        _eventData,
        {
          searchText: currentSearchQuery,
          page: window.location.pathname,
        },
        'teams_landing_search_query_updated',
        'search'
      );
    }
  }

  updateSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    this.setState({ currentSearchInput: query });
  };

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.setState({ currentSearchQuery: this.state.currentSearchInput });
    this.props.onSearchQueryChange?.(this.state.currentSearchInput);
  };

  updateSearchQueryToDomain = (domain: string) => () => {
    const { onSearchQueryChange } = this.props;
    const domainAsSearchQuery = COURSERA_PLUS_POPULAR_DOMAINS_MAPPING.find(
      (currentDomain) => currentDomain.id === domain.toLowerCase()
    )?.name;

    this.setState({ currentSearchInput: domainAsSearchQuery });
    this.setState({ currentSearchQuery: domainAsSearchQuery });
    if (domainAsSearchQuery) {
      onSearchQueryChange?.(domainAsSearchQuery);
    }
  };

  render() {
    const { currentSearchQuery, currentSearchInput } = this.state;

    return (
      <TrackedDiv trackingName="teams_landing_search" withVisibilityTracking={true} trackClicks={false}>
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <form onSubmit={this.handleSubmit} css={{ width: '100%', maxWidth: 704 }}>
            <TextField
              fullWidth
              label={_t('Search query')}
              prefix={<SearchIcon />}
              renderLabel={(label) => <div css={{ display: 'none' }}>{label}</div>}
              value={currentSearchInput}
              onChange={this.updateSearchInput}
              placeholder={_t('e.g. Data Science')}
            />
          </form>
          <div css={{ marginTop: 24, marginBottom: 24 }}>
            <ChipGroup label={_t('Popular')} inlineLabel>
              {rearrangedDomainMaps.map((domain) => (
                <Chip
                  key={domain.id}
                  onClick={this.updateSearchQueryToDomain(domain.id)}
                  selected={domain.name === currentSearchQuery}
                >
                  {domain.name}
                </Chip>
              ))}
            </ChipGroup>
          </div>
        </div>
        {currentSearchQuery && (
          <div
            // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
            {...css(styles.searchResults)}
          >
            <div className="tab-contents">
              <NumberOfResultsSection
                currentSearchQuery={currentSearchQuery}
                limitNumberOfResults={NUMBER_OF_SEARCH_RESULTS}
                searching={this.props.searching}
                hits={this.props.results ?? []}
              />
              <InPageSearchResults
                numberOfResults={NUMBER_OF_SEARCH_RESULTS}
                searching={this.props.searching}
                hits={this.props.results ?? []}
              />
            </div>
          </div>
        )}
      </TrackedDiv>
    );
  }
}

export default React.memo(CourseSearch);
